import React, {ReactNode} from 'react';
import cn from 'classnames';

import {isTranslate} from 'shared/lib/lang';

import {useSliderDrag} from './useSliderDrag';

import styles from './Slider.module.scss';

export interface ISliderMarker {
    key: string | number,
    position: number,
    label: ReactNode,
    overlapped: boolean,
}

interface ISliderProps {
    name: string,
    value: number,
    min: number,
    max: number,
    progressPct: number,
    fillPct: number,
    markers: ISliderMarker[],
    thumbLabel: ReactNode,
    disabled: boolean,
    onOffset: (_offset: number, _width: number) => void,
}

const TRANSITION = '0.3s cubic-bezier(0.4, 0, 0.2, 1)';

const Slider: React.FC<ISliderProps> = ({
    name,
    value,
    min,
    max,
    progressPct,
    fillPct,
    markers,
    thumbLabel,
    disabled,
    onOffset,
}) => {
    const {trackRef, dragging, handleMouseDown, handleTouchStart} = useSliderDrag({disabled, onOffset});

    return (
        <div className={cn(
            styles.container,
            styles[isTranslate() ? 'enColorsContainer' : 'ruColorsContainer'],
        )}>
            <div
                ref={trackRef}
                className={styles.track}
                onMouseDown={handleMouseDown}
                onTouchStart={handleTouchStart}
                aria-disabled={disabled}
                role="slider"
                aria-valuemin={min}
                aria-valuemax={max}
                aria-valuenow={value}
            >
                <div
                    className={styles.gradientFill}
                    style={{
                        width: `${fillPct}%`,
                        transition: dragging ? 'none' : `width ${TRANSITION}`,
                    }}
                />

                {markers.map((marker) => (
                    <div
                        key={marker.key}
                        className={styles.number}
                        style={{left: `${marker.position}%`}}
                    >
                        <span className={marker.overlapped ? styles.numberOverlapped : ''}>
                            {marker.label}
                        </span>
                    </div>
                ))}

                <div
                    className={styles.thumb}
                    style={{
                        left: `calc(${progressPct}% - ${progressPct * 0.52}px)`,
                        transition: dragging ? 'none' : `left ${TRANSITION}`,
                    }}
                >
                    {thumbLabel}
                </div>
            </div>

            <input type="hidden" name={name} value={value}/>
        </div>
    );
};

export default Slider;
